import { useEffect, useState } from "react";
import {
  LogOut as LogOutIcon,
  LogIn as LogInIcon,
  AlertCircle as AlertCircleIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  useSessionStore,
  selectSessionExpired,
  selectExpiredReason,
} from "@/stores/sessionStore";
import styles from "./SessionExpiredModal.module.css";

const AUTO_REDIRECT_SECONDS = 5;
const LOGIN_PATH = "/auth/login";

export default function SessionExpiredModal() {
  const { t } = useTranslation();
  const isVisible = useSessionStore(selectSessionExpired);
  const expiredReason = useSessionStore(selectExpiredReason);
  const reset = useSessionStore((s) => s.reset);
  const [secondsLeft, setSecondsLeft] = useState(AUTO_REDIRECT_SECONDS);

  useEffect(() => {
    if (!isVisible) return;
    setSecondsLeft(AUTO_REDIRECT_SECONDS);
    const interval = window.setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          window.clearInterval(interval);
          redirectToLogin();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(interval);
  }, [isVisible]);

  function redirectToLogin() {
    reset();
    window.location.href = LOGIN_PATH;
  }

  function handleLoginAgain() {
    redirectToLogin();
  }

  if (!isVisible) return null;

  const sessionMessage =
    expiredReason?.message ||
    t(
      "auth.session.defaultMessage"
    );

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true">
      <div className={styles.content}>
        <div className={styles.icon}>
          <LogOutIcon size={48} />
        </div>

        <h2 className={styles.title}>
          {t("auth.session.expired")}
        </h2>

        <p className={styles.message}>{sessionMessage}</p>

        {expiredReason ? (
          <div className={styles.details}>
            <div className={styles.reasonBadge}>
              <AlertCircleIcon size={16} />
              <span>
                {expiredReason.type === "license_limit"
                  ? t(
                      "auth.session.licenseLimit"
                    )
                  : expiredReason.type === "concurrent_login"
                    ? t(
                        "auth.session.concurrentLogin"
                      )
                    : expiredReason.type === "permission_error"
                      ? t(
                          "auth.session.permissionError"
                        )
                      : t("auth.session.unknown")}
              </span>
            </div>
          </div>
        ) : null}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.btn}
            onClick={handleLoginAgain}
          >
            <LogInIcon size={18} />
            {t("auth.session.loginAgain")}
          </button>
        </div>

        <p className={styles.info}>
          {t(
            "auth.session.info"
          )}
          <br />
          <strong className={styles.countdown}>
            Você será redirecionado automaticamente em {secondsLeft} segundo
            {secondsLeft === 1 ? "" : "s"}...
          </strong>
        </p>
      </div>
    </div>
  );
}
